import { type RefObject, useEffect, useRef } from "react";

import { m } from "@vega-core/paraglide/messages";

type Props = {
  isLoadingMore: boolean;
  onLoadMore: () => void;
  loadingLabel?: string;
  scrollRootRef?: RefObject<HTMLElement | null>;
};

export const NotificationCenterLoadMoreSentinel = ({
  isLoadingMore,
  onLoadMore,
  loadingLabel = m.notificationCenter_loading(),
  scrollRootRef,
}: Props) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const isLoadingMoreRef = useRef(isLoadingMore);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
    isLoadingMoreRef.current = isLoadingMore;
  });

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoadingMoreRef.current) {
          onLoadMoreRef.current();
        }
      },
      { root: scrollRootRef?.current ?? null, threshold: 0.1 },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [scrollRootRef]);

  return (
    <div
      aria-hidden={!isLoadingMore}
      className="px-4 py-2 body-small-regular text-text/secondary text-center"
      data-testid="notification-center-load-more-sentinel"
      ref={sentinelRef}
    >
      {isLoadingMore ? loadingLabel : null}
    </div>
  );
};
